import React, { useEffect } from 'react';
import Table from 'Components/Table';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { keyBy } from 'lodash';
import { RoleService } from 'Services';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import useSplitviewForm from 'Components/Table/hooks/useSplitViewForm';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { QF_MODIFIED, QF_STATUS } from 'Hooks/rule-quickfilters/helper';

import { useQueryParam } from 'Hooks/useQueryParam';
import SplitViewContent from './SplitViewContent';

import styles from './TenantGroupRulesTable.css';

type Mode = 'tenantOverrideSelection';

type Props = {
    globalRules: GlobalRulesData;
    tenantOverrideRules: GlobalRulesData;
    columnNames: Column<Rule>[];
    mode: Mode;
    tableName?: string;
    isLoading?: boolean;
};

const TABLEID = 'tenant-group-rules-table';

const TenantGroupLandlordTable = ({
    globalRules,
    tenantOverrideRules,
    columnNames,
    mode,
    tableName,
    isLoading = false
}: Props) => {
    const { t } = useTranslation();
    useStickyTableHead(TABLEID);
    const columns = React.useMemo<Column<Rule>[]>(() => columnNames, [columnNames]);
    const {
        splitViewFormState,
        setIsOpen,
        setInitialFormState,
        setRowId
    } = useSplitviewForm<MerchantRuleSplitViewFormState>();
    const updateSplitViewId = React.useCallback(
        (val) => {
            setRowId?.(val);
            setIsOpen?.(false);
        },
        [setRowId, setIsOpen]
    );

    const quickFilters = useGetRuleQuickFilters({
        quickFilters: [QF_MODIFIED.id, QF_STATUS.id],
        initialFilters: [
            {
                id: QF_STATUS.id,
                activeId: []
            }
        ]
    });

    const {
        parsed: { selectedRowId }
    } = useQueryParam();

    useEffect(() => {
        setRowId?.(selectedRowId);
    }, [selectedRowId, setRowId]);

    const tenantOverrideRulesKeyed = keyBy(tenantOverrideRules?.rules, 'id');
    const tenantGroupRulesData =
        tenantOverrideRules?.rules.map((rule) => {
            return {
                ...rule,
                // overrideGroup: rule.id in globalRuleKeyed ? t('Overrides') : ''
                overrideGroup: t('Overrides')
            };
        }) || [];
    const tenantTableData = React.useMemo(() => tenantGroupRulesData, [tenantGroupRulesData]);
    const landlordRulesData = globalRules?.rules.filter((rule) => !(rule.id in tenantOverrideRulesKeyed));
    const landlordTableData = React.useMemo(() => landlordRulesData, [landlordRulesData]);

    return (
        <div className={styles.tableContainer}>
            <Table
                tableName={tableName}
                isLoading={isLoading}
                showMultiSort
                showRichFilter
                manualSplitView
                controlledSplitViewId={splitViewFormState.rowId}
                updateSplitViewId={updateSplitViewId}
                showExpandableSearch
                icon={{
                    showIcon: true
                }}
                expand={{
                    canExpand: true,
                    expandAll: true
                }}
                initialState={{
                    groupBy: ['ruleGroup'],
                    sortBy: [{ id: 'id', desc: false }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                dataTestId={TABLEID}
                columns={columns}
                noDataMessage='noTenantGroupRules'
                data={mode === 'tenantOverrideSelection' ? landlordTableData : tenantTableData}
                clickableRow
                splitViewContent={(props) => (
                    <SplitViewContent
                        setRowId={setRowId}
                        setInitialFormState={setInitialFormState}
                        splitViewFormState={splitViewFormState}
                        setIsOpen={setIsOpen}
                        dataTestId='tenantGroup'
                        globalRules={globalRules}
                        readOnly={!RoleService.hasEditRuleOrBulkUploadAccess}
                        {...props}
                    />
                )}
                quickFilters={quickFilters.filters}
            />
        </div>
    );
};

export default TenantGroupLandlordTable;
